import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Crown, Mail, User, Heart, Settings, Bell, Menu, Users, Plus, MessageCircle, Zap, Check, Pencil, Calendar, ChevronRight, Coins, HelpCircle, LogOut, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { CredentialsSettings } from "@/components/CredentialsSettings";
import { apiClient } from "@/api/ApiClient";
import { isDebug } from "@/config/env";
interface MyProfileProps {
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  onNavigateToExplore?: () => void;
  onNavigateToPremium?: () => void;
  onNavigateToHelpCenter?: () => void;
  onNavigateToContactSupport?: () => void;
  onLogout?: () => void;
  user: {
    id?: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
    gender?: 'male' | 'female' | 'non-binary';
    age?: number;
    interested_in?: 'men' | 'women' | 'both';
    notifications_allowed?: boolean;
  };
  onTokensClick?: () => void;
  userTokens?: number;
  onPurchaseTokens?: (tokens: number, paymentMethod: string) => void;
}
export const MyProfile = ({
  onNavigateToChat,
  onNavigateToMyBoyfriends,
  onNavigateToSettings,
  onCreateNew,
  onNavigateToExplore,
  onNavigateToPremium,
  onNavigateToHelpCenter,
  onNavigateToContactSupport,
  onLogout,
  user,
  onTokensClick,
  userTokens,
  onPurchaseTokens
}: MyProfileProps) => {
  const { t } = useTranslation();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean | null>(null); // null = loading, true/false = loaded
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<Array<{
    transactionType: string;
    amount: number;
    balanceBefore: number;
    balanceAfter: number;
    description?: string;
    createdAt?: string;
  }>>([]);
  const { toast } = useToast();
  const [displayTokens, setDisplayTokens] = useState<number>(userTokens || 0);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    age: 25,
    gender: null as 'male' | 'female' | 'non-binary' | null,
    interested_in: null as 'men' | 'women' | 'both' | null
  });

  // Load profile data when user object changes
  useEffect(() => {
    if (user?.id) {
      setProfile({
        name: user.name || "",
        email: user.email || "",
        age: user.age || 25,
        gender: user.gender || null,
        interested_in: user.interested_in || null
      });
      setIsProfileLoaded(true);
    }
  }, [user?.id, user?.name, user?.email, user?.age, user?.gender, user?.interested_in]);

  // Keep displayed tokens in sync with prop
  useEffect(() => {
    setDisplayTokens(userTokens || 0);
  }, [userTokens]);

  // Load last 3 token transactions for authenticated users
  useEffect(() => {
    const loadTransactions = async () => {
      // try whenever we likely have a session (has access token or server user id)
      if (!user?.id) return;
      try {
        const data = await apiClient.getMyTokenTransactions(3);
        if (Array.isArray(data)) {
          setRecentTransactions(data);
        }
      } catch (e) {
        // silent fail
      }
    };
    loadTransactions();
    // also refresh when token balance changes (e.g., after purchase)
  }, [user?.id, userTokens]);

  // Update notifications state when user data loads from API
  useEffect(() => {
    if (user?.notifications_allowed !== undefined) {
      setNotificationsEnabled(user.notifications_allowed);
    } else if (user?.id) {
      // If user exists but notifications_allowed is undefined, default to true
      setNotificationsEnabled(true);
    }
  }, [user?.notifications_allowed, user?.id]);

  const handleSaveProfile = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "Unable to save profile. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Save profile to API if user has email (authenticated)
      if (user.email) {
        await apiClient.updateUserProfile({
          name: profile.name,
          gender: profile.gender,
          interestedInMen: profile.interested_in === 'men' || profile.interested_in === 'both',
          interestedInWomen: profile.interested_in === 'women' || profile.interested_in === 'both'
        });
      }
      
      toast({
        title: "Success",
        description: "Profile updated successfully!",
      });
      
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Error",
        description: "Failed to save profile changes. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNotificationToggle = async (enabled: boolean) => {
    if (notificationsEnabled === null) return; // Don't allow changes while loading
    
    setNotificationsEnabled(enabled);
    
    if (!user?.id) return;

    try {
      // Save to API if user has email (authenticated)
      if (user.email) {
        const response = await apiClient.updateNotifications(enabled);
        
        if (response.success) {
          toast({
            title: "Success",
            description: `Notifications ${enabled ? 'enabled' : 'disabled'} successfully!`,
          });
        } else {
          throw new Error(response.message || "Failed to update notifications");
        }
      } else {
        // For local users without email, just show success message
        toast({
          title: "Success",
          description: `Notifications ${enabled ? 'enabled' : 'disabled'} successfully!`,
        });
      }
    } catch (error) {
      console.error('Error updating notifications:', error);
      // Revert the state on error
      setNotificationsEnabled(!enabled);
      toast({
        title: "Error",
        description: "Failed to update notification settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    toast({
      title: "Payment Successful",
      description: `You've successfully upgraded to the ${plan} plan!`,
    });
    setShowPaymentModal(false);
  };

  const handleLogout = () => {
    // Clear all localStorage data
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
    
    // Call the logout callback
    if (onLogout) {
      onLogout();
    }
    
    toast({
      title: "Logged out successfully",
      description: "You have been logged out and all local data has been cleared.",
    });
  };
  // Show loading state if profile is not loaded yet
  if (!isProfileLoaded) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        {/* Header */}
        <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0">
          <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
            {t('profile.my_profile')}
          </h1>
        </div>
        
        {/* Loading Content */}
        <div className="p-4 space-y-6">
          <Card className="p-6 border-primary/20">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-muted rounded-full animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-6 bg-muted rounded animate-pulse w-32" />
                <div className="h-4 bg-muted rounded animate-pulse w-48" />
                <div className="h-4 bg-muted rounded animate-pulse w-24" />
              </div>
            </div>
          </Card>
          
          <Card className="p-6 border-primary/20">
            <div className="space-y-3">
              <div className="h-6 bg-muted rounded animate-pulse w-40" />
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded animate-pulse w-full" />
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0">
        <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
          {t('profile.my_profile')}
        </h1>
      </div>
      
      <div className="p-4 md:p-6 overflow-y-auto max-w-full">
        <div className="max-w-2xl mx-auto w-full px-2 md:px-0">

          {/* Premium Upgrade Banner - Show at top for non-premium users */}
          {!user.isPremium && <div className="mb-6">
              <div className="flex flex-col p-3 bg-gradient-to-br from-purple-900 to-pink-900 rounded-lg text-white space-y-2">
                <div>
                <h4 className="font-semibold text-center mb-4 flex items-center justify-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-400" />
                  {t('profile.upgrade_to_premium')}
                </h4>
                
                {/* Premium Features */}
                <div className="grid grid-cols-2 gap-1 text-sm mb-2">
                  <div className="flex items-center gap-2">
                    <span>💬</span>
                    <span>{t('profile.unlimited_chats')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🔥</span>
                    <span>{t('profile.private_photos')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🎤</span>
                    <span>{t('profile.voice_messages')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🔒</span>
                    <span>{t('profile.premium_bfs')}</span>
                  </div>
                </div>
                </div>
                
                {/* Membership Options */}
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPlan === 'monthly' 
                        ? 'border-white bg-white/10' 
                        : 'border-white/30 hover:border-white/50'
                    }`}
                    onClick={() => setSelectedPlan('monthly')}
                  >
                    <div className="text-center">
                      <div className="font-semibold">{t('profile.monthly')}</div>
                      <div className="text-2xl font-bold">$9.99</div>
                      <div className="text-sm opacity-80">{t('profile.per_month')}</div>
                    </div>
                  </div>
                  
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all relative ${
                      selectedPlan === 'yearly' 
                        ? 'border-white bg-white/10' 
                        : 'border-white/30 hover:border-white/50'
                    }`}
                    onClick={() => setSelectedPlan('yearly')}
                  >
                    <div className="absolute -top-2 -right-2 bg-white text-primary text-xs px-2 py-1 rounded-full font-bold">
                      {t('profile.save_50')}
                    </div>
                    <div className="text-center">
                      <div className="font-semibold">{t('profile.yearly')}</div>
                      <div className="text-2xl font-bold">$59.99</div>
                      <div className="text-sm opacity-80">{t('profile.per_year')}</div>
                    </div>
                  </div>
                </div>
                
                {/* Upgrade Button at Bottom */}
                <Button 
                  variant="romantic" 
                  className="w-full mt-4" 
                  onClick={() => setShowPaymentModal(true)}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {t('profile.upgrade_now')}
                </Button>
              </div>
            </div>}

        {/* Profile Card */}
        <Card className="p-6 mb-6 border-primary/20">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1">
              {isEditingProfile ? <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('profile.name')}</Label>
                    <Input id="name" value={profile.name} onChange={e => setProfile(prev => ({
                    ...prev,
                    name: e.target.value
                  }))} placeholder={t('profile.name')} className="text-lg" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">{t('profile.gender')}</Label>
                    <Select value={profile.gender || ""} onValueChange={value => setProfile(prev => ({
                    ...prev,
                    gender: value as any
                  }))}>
                      <SelectTrigger className="text-lg">
                        <SelectValue placeholder={t('profile.select_gender')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">{t('profile.gender_female_emoji')}</SelectItem>
                        <SelectItem value="male">{t('profile.gender_male_emoji')}</SelectItem>
                        <SelectItem value="non-binary">{t('profile.gender_nonbinary_emoji')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interested-in">{t('profile.interested_in')}</Label>
                    <Select value={profile.interested_in || ""} onValueChange={value => setProfile(prev => ({
                    ...prev,
                    interested_in: value as any
                  }))}>
                      <SelectTrigger className="text-lg">
                        <SelectValue placeholder={t('profile.interested_in_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="men">{t('profile.interested_men_emoji')}</SelectItem>
                        <SelectItem value="women">{t('profile.interested_women_emoji')}</SelectItem>
                        <SelectItem value="both">{t('profile.interested_both_emoji')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="romantic" size="sm" onClick={handleSaveProfile}>
                      {t('profile.save_changes')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setIsEditingProfile(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div> : <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold">
                      {profile.name || "User"}
                    </h2>
                    {user.isPremium && <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-amber-400 hover:from-amber-600 hover:to-yellow-600">
                        <Crown className="h-3 w-3 mr-1" />
                        {t('profile.premium')}
                      </Badge>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingProfile(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    {t('profile.edit')}
                  </Button>
                </div>}
              {!isEditingProfile && <>
                  {profile.email && <p className="text-muted-foreground flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {profile.email}
                    </p>}
                   {profile.gender && <p className="text-muted-foreground flex items-center gap-1 mt-1">
                       <User className="h-4 w-4" />
                       {t('profile.gender')}: {profile.gender === 'female' ? t('profile.gender_female_emoji') : profile.gender === 'male' ? t('profile.gender_male_emoji') : t('profile.gender_nonbinary_emoji')}
                     </p>}
                   {profile.interested_in && <p className="text-muted-foreground flex items-center gap-1 mt-1">
                       <Heart className="h-4 w-4" />
                       {t('profile.interested_in_label')}: {profile.interested_in === 'men' ? t('profile.interested_men_emoji') : profile.interested_in === 'women' ? t('profile.interested_women_emoji') : t('profile.interested_both_emoji')}
                     </p>}
                </>}
             </div>
           </div>

           {/* Tokens Balance Section */}
           <div className="mt-4 pt-4 border-t border-primary/10">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20 h-9">
                  <Coins className="h-4 w-4 text-amber-500" />
                  <span className="font-semibold">{displayTokens}</span>
                  <span className="text-muted-foreground text-sm">{t('profile.tokens')}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onTokensClick}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-amber-400 hover:from-amber-600 hover:to-yellow-600 hover:shadow-lg transition-all duration-200 hover:scale-105"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('profile.add_tokens')}
                </Button>
              </div>
             {isDebug && (
               <div className="mt-2">
                 <Button
                   variant="outline"
                   size="sm"
                   className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                   onClick={async () => {
                     try {
                       const res = await apiClient.removeAllTokens();
                       if (res?.success === false) {
                         throw new Error(res?.message || 'Failed to remove tokens');
                       }
                       // Optimistically update local display and reload recent transactions
                       setDisplayTokens(0);
                       try {
                         const data = await apiClient.getMyTokenTransactions(3);
                         if (Array.isArray(data)) {
                           setRecentTransactions(data);
                         }
                       } catch {}
                       // Notify app to refresh token balance
                       try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
                       toast({ title: 'Tokens removed', description: 'Your token balance is now 0.' });
                     } catch (e) {
                       toast({ title: 'Error', description: 'Failed to remove tokens.', variant: 'destructive' });
                     }
                   }}
                 >
                   Remove tokens (debug)
                 </Button>
               </div>
             )}
            {/* Recent token transactions */}
            {recentTransactions.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  {t('profile.last_transactions')}
                </div>
                <div className="space-y-1">
                  {recentTransactions.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm border border-primary/10 rounded px-2 py-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${t.amount < 0 ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600'}`}>
                          {t.amount > 0 ? `+${t.amount}` : t.amount}
                        </span>
                        <span className="text-muted-foreground capitalize">{t.transactionType?.toLowerCase()}</span>
                        {t.description && <span className="text-muted-foreground">{t.description}</span>}
                      </div>
                      <div className="text-muted-foreground">{t.balanceBefore} → <span className="font-medium text-foreground">{t.balanceAfter}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>

        </Card>

        {/* Settings Section */}
        <Card className="p-6 mb-6 border-primary/20">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t('profile.settings_section')}
          </h3>
          
          {/* Account Security (Change password/email) */}
          <div className="mb-6">
            <h4 className="font-medium mb-2">{t('profile.account_security')}</h4>
            <CredentialsSettings user={user} />
          </div>

          <div className="space-y-3">
            {/* Add Email Section - Only show if user doesn't have email */}
            {!user?.email && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={() => setShowEmailModal(true)}>
                <div className="flex-1">
                  <h4 className="font-medium">{t('profile.add_email')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('profile.add_email_desc')}
                  </p>
                </div>
                <div className="ml-4">
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            )}
            
            {/* Language Selection */}
            <LanguageSwitcher />
            
            {/* Notifications Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
               <div className="flex items-center gap-2 flex-1">
                 <h4 className="font-medium">{t('profile.notifications')}</h4>
                 {notificationsEnabled === null && (
                   <span className="text-xs text-muted-foreground">{t('profile.loading')}</span>
                 )}
               </div>
              {notificationsEnabled !== null ? (
                <Switch
                  checked={notificationsEnabled}
                  onCheckedChange={handleNotificationToggle}
                />
              ) : (
                <div className="w-10 h-6 bg-muted rounded-full animate-pulse" />
              )}
            </div>
            
          </div>
        </Card>

        {/* Support Section */}
        <Card className="p-6 mb-6 border-primary/20">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {t('profile.support_section')}
          </h3>
          
          {/* Feedback Button */}
          <div className="mb-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={onNavigateToContactSupport}>
              <div className="flex-1">
                <h4 className="font-medium">{t('profile.send_feedback')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('profile.send_feedback_desc')}
                </p>
              </div>
              <div className="ml-4">
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={onNavigateToHelpCenter}>
              <div className="flex-1">
                <h4 className="font-medium">{t('profile.help_center')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('profile.help_center_desc')}
                </p>
              </div>
              <div className="ml-4">
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={onNavigateToContactSupport}>
              <div className="flex-1">
                <h4 className="font-medium">{t('profile.contact_support')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('profile.contact_support_desc')}
                </p>
              </div>
              <div className="ml-4">
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={() => {}}>
              <div className="flex-1">
                <h4 className="font-medium">{t('profile.terms_of_service')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('profile.terms_desc')}
                </p>
              </div>
              <div className="ml-4">
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer" onClick={() => {}}>
              <div className="flex-1">
                <h4 className="font-medium">{t('profile.privacy_policy')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('profile.privacy_desc')}
                </p>
              </div>
              <div className="ml-4">
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        </Card>

        {/* Account Actions */}
        <Card className="p-6 mb-6 border-0">
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 bg-black text-white border-white hover:bg-black/90 hover:text-white hover:border-white"
              onClick={() => setShowLogoutConfirmation(true)}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('profile.log_out')}
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('profile.delete_account')}
            </Button>
          </div>
        </Card>

        </div>
      </div>

      {/* Premium Modal */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent className="sm:max-w-2xl h-screen overflow-y-auto">
          <h1 className="text-center text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent mb-0 leading-tight">
            {t('premium_modal.upgrade_romance')}
          </h1>
          <div className="space-y-2 mt-0">
            {/* Urgency Banner */}
            <div className="border-2 border-orange-500 bg-transparent text-orange-500 p-3 rounded-lg text-center mt-0">
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">⏰</span>
                <span className="font-bold text-sm">{t('premium_modal.limited_time_offer')}</span>
              </div>
            </div>

            {/* Features Section */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center mb-4">
                <Zap className="h-8 w-8 mx-auto mb-2" />
                <h2 className="text-lg font-bold mb-2">{t('premium_modal.premium_features')}</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unblurred_photos')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.hd_images')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.priority_responses')}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.free_tokens')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.generate_spicy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unlock_premium_bfs')}</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Pricing Plans */}
            <div className="grid grid-cols-2 gap-3">
              <Card className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 ${selectedPlan === 'monthly' ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' : 'border-primary/20 hover:border-primary/40'}`} onClick={() => setSelectedPlan('monthly')}>
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.monthly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$19.99</p>
                  <p className="text-2xl font-bold text-primary">$9.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_month')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50')}</p>
                {selectedPlan === 'monthly' && <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>}
              </Card>

              <Card className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${selectedPlan === 'yearly' ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' : 'border-primary/20 hover:border-primary/40'}`} onClick={() => setSelectedPlan('yearly')}>
                <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                    {t('premium_modal.best_value')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.yearly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$159.99</p>
                  <p className="text-2xl font-bold text-primary">$79.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_year')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50_plus')}</p>
                {selectedPlan === 'yearly' && <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>}
              </Card>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                size="lg" 
                className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={onNavigateToPremium}
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('premium_modal.upgrade_to_plan', { plan: selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan') })}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t('premium_modal.secure_payment_cancel')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Email Address</DialogTitle>
            <DialogDescription>
              Adding an email helps secure your account and enables password recovery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Create Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                className="w-full"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowEmailModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="romantic"
                className="flex-1"
                onClick={() => {
                  // Handle email submission here
                  toast({
                    title: "Email Added",
                    description: "Your email has been successfully added to your account.",
                  });
                  setShowEmailModal(false);
                }}
              >
                Add Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Payment Modal */}
      <SubscriptionPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        selectedPlan={selectedPlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutConfirmation} onOpenChange={setShowLogoutConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600">Log Out</DialogTitle>
            <DialogDescription className="text-center">
              {!user?.email ? (
                <div className="space-y-2">
                  <p>Are you sure you want to log out?</p>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⚠️ Warning: Without an email, you will lose all your account data permanently!
                    </p>
                  </div>
                </div>
              ) : (
                "Are you sure you want to log out?"
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowLogoutConfirmation(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="outline" 
              className="w-full text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300"
              disabled={isLoggingOut}
              onClick={() => {
                setIsLoggingOut(true);
                setShowLogoutConfirmation(false);
                handleLogout();
                setIsLoggingOut(false);
              }}
            >
              {isLoggingOut ? 'Logging out...' : 'Log Out'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Account</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to delete your account?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="outline" 
              className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              disabled={isDeletingAccount}
              onClick={async () => {
                if (!user?.id) return;
                setIsDeletingAccount(true);
                try {
                  const response = await apiClient.deleteAccount();
                  if (response?.success === false) {
                    throw new Error(response?.message || 'Failed to delete account');
                  }
                  try { sessionStorage.setItem('logoutReason', 'deleted'); } catch (err) { /* ignore */ }
                  setShowDeleteConfirmation(false);
                  if (onLogout) onLogout();
                } catch (err) {
                  console.error('Error deleting account:', err);
                  setShowDeleteConfirmation(false);
                  toast({ title: 'Error', description: 'Failed to delete account. Please try again.', variant: 'destructive' });
                } finally {
                  setIsDeletingAccount(false);
                }
              }}
            >
              {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};